/* External */
import {
  useRef, useEffect, memo,
} from 'react';
import PropTypes from 'prop-types';

/* Utils */
import { useScrollIntoView, ScrollPosition, ScrollMode } from '../ScrollView';
import withCursorVisibilityFilterFocusable from '../../hocs/withCursorVisibilityFilter.hoc';

function ScrollIntoViewIfFocused({
  position,
  focused,
  hasFocusedChild,
  className,
  style,
  children,
  cursorVisibilityChanged,
  assignRef,
}) {
  const cursorVisibilityChangedRef = useRef(cursorVisibilityChanged);
  const ref = useRef();
  const scrollIntoView = useScrollIntoView();

  cursorVisibilityChangedRef.current = cursorVisibilityChanged;

  useEffect(() => {
    if ((hasFocusedChild || focused) && ref?.current) {
      scrollIntoView(ref.current, {
        position,
        mode: cursorVisibilityChangedRef.current ? ScrollMode.IF_NEEDED : ScrollMode.ALWAYS,
      });
    }
  }, [focused, hasFocusedChild, position, scrollIntoView]);

  useEffect(() => {
    assignRef(ref.current);
  });

  return (
    <div className={className} ref={ref} style={style}>{children}</div>
  );
}

ScrollIntoViewIfFocused.propTypes = {
  focused: PropTypes.bool,
  hasFocusedChild: PropTypes.bool,
  cursorVisibilityChanged: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.objectOf(PropTypes.string),
  children: PropTypes.node.isRequired,
  assignRef: PropTypes.func,
  position: PropTypes.oneOf([
    ScrollPosition.START,
    ScrollPosition.END,
    ScrollPosition.CENTER,
  ]),
};

ScrollIntoViewIfFocused.defaultProps = {
  focused: false,
  hasFocusedChild: false,
  cursorVisibilityChanged: false,
  className: undefined,
  style: undefined,
  position: ScrollPosition.START,
  assignRef: () => {},
};

export default memo(
  withCursorVisibilityFilterFocusable({
    trackChildren: true,
    autoRestoreFocus: false,
    forgetLastFocusedChild: true,
  })(
    memo(ScrollIntoViewIfFocused),
  ),
);
